import { useCallback, useContext, useMemo, useRef } from 'react';
import { FacetFilteringContext } from '@sls-react/contexts/facetFiltering/FacetFilteringContext';
import {
  BaseFacet,
  BaseValueFacet,
  FilterState,
  FlattenedFilterState,
  TypedRailsFacet,
  TypedRailsFacetChild,
} from '@sls-react/typings/services/rails/facets';
import removeFromState from '@sls-react/utils/removeFromState';
import addToState from '@sls-react/utils/addToState';
import filterStateToQueryString from '@sls-react/utils/filterStateToQueryString';
import { useRouter } from '@sls-react/routing';
import getActiveFacetsCount from '@sls-react/contexts/facetFiltering/getActiveFacetsCount';
import { fetchFacetMetaNoItems } from '@sls-react/api/items';
import filterStateToQueryStringNoCategory from '@sls-react/utils/filterStateToLanderQueryString';
import { RailsCategory } from '@sls-react/typings/services/rails/item';
import { getDetailById } from '@sls-react/utils/getDetailById';
import flattenFilterState from '@sls-react/contexts/facetFiltering/flattenFilterState';
import deepmergeWithArrays from '@sls-react/utils/deepmergeWithArrays';
import { useSavedSearchContext } from '@sls-react/contexts/SavedSearchContext';
import { useUserSearchFiltersContext } from '@sls-react/contexts/UserSearchFilters';
import { reduceEnabledSearchFilters } from '@sls-react/api/user';
import getTransferableFilterState from '@sls-react/utils/getTransferableFilterState';
import { fireAppliedFilter } from '@sls-react/services/analytics/events/search';
import { useCookie } from 'react-use';
import { KnownCookie } from '@sls-react/typings/cookies';
import useMobile from '@sls-react/hooks/useMobile';

const strippedProperties = {
  '{_branded}': undefined,
  gclid: undefined,
  fbclid: undefined,
  min_published_at: undefined,
  page: undefined,
  saved_search_id: undefined,
  src: undefined,
  stid: undefined,
  track: undefined,
  utm_campaign: undefined,
  utm_content: undefined,
  utm_medium: undefined,
  utm_source: undefined,
};

export default function useFacetFiltering() {
  const router = useRouter();
  const mobile = useMobile();
  const {
    state: {
      filterState,
      facets,
      inModal,
      lander,
      paging,
      similarToItem,
      sorts,
      type,
    },
    dispatch,
  } = useContext(FacetFilteringContext);
  const { savedSearch } = useSavedSearchContext();
  const { filters } = useUserSearchFiltersContext();
  const facetUpdateQuery = useRef(filterState);
  const [fastShippingCookie] = useCookie(
    'fast_shipping_filter_enabled' as KnownCookie,
  );
  const [canadianSellersCookie] = useCookie(
    'canadian_sellers_filter_enabled' as KnownCookie,
  );

  const navigateToUpdatedFilterState = useCallback(
    (newState: FlattenedFilterState, replace?: boolean) => {
      const split = router.asPath.split('?');
      let stringified: string;

      if (type === 'locker') {
        stringified = filterStateToQueryString(
          newState,
          ['seller', 'sort'],
          sorts?.default_option,
        );
      } else {
        stringified = filterStateToQueryStringNoCategory(
          newState,
          [],
          sorts?.default_option,
        );
      }

      if (replace) {
        router.replace(router.route + stringified, split[0] + stringified, {
          scroll: mobile,
        });
      } else {
        router.push(router.route + stringified, split[0] + stringified, {
          scroll: mobile,
        });
      }
    },
    [router, sorts?.default_option, type],
  );

  const unflattenedFilterState = useMemo(
    () =>
      deepmergeWithArrays([
        lander?.query,
        savedSearch?.query,
        filterState,
        reduceEnabledSearchFilters(
          filters,
          fastShippingCookie,
          canadianSellersCookie,
        ),
      ]) as FilterState,
    [
      filterState,
      filters,
      lander?.query,
      savedSearch?.query,
      fastShippingCookie,
      canadianSellersCookie,
    ],
  );

  const evaluatedFilterState = useMemo(
    () => flattenFilterState(unflattenedFilterState),
    [unflattenedFilterState],
  );

  const isSelected = useCallback(
    (
      facet: BaseFacet | string,
      value: BaseValueFacet | string[] | number[] | string | number,
    ): boolean => {
      let filterStateElement: any[];
      let filterStateValue: string | number;

      if (typeof facet === 'string') {
        filterStateElement = evaluatedFilterState[facet];
      } else {
        filterStateElement = evaluatedFilterState[facet.slug];
      }

      if (typeof value === 'string' || typeof value === 'number') {
        filterStateValue = value;
      } else if (Array.isArray(value)) {
        filterStateValue = value[0];
      } else {
        filterStateValue = value.id;
      }

      if (filterStateElement) {
        if (Array.isArray(filterStateElement)) {
          return !!filterStateElement.find(e => e == filterStateValue);
        } else {
          return filterStateElement == filterStateValue;
        }
      } else {
        return false;
      }
    },
    [evaluatedFilterState],
  );

  const changeFacetSelection = useCallback(
    (
      facet: BaseFacet | string,
      value: BaseValueFacet | string[] | number[] | string | number | null,
      singleValue?: boolean,
      navigate = true,
    ) => {
      let newState: FlattenedFilterState = {
        ...filterState,
        ...strippedProperties,
      };

      let facetSlug;
      let newValue;

      if (typeof facet === 'string') {
        facetSlug = facet;
      } else {
        facetSlug = facet.slug;
      }

      fireAppliedFilter({
        detail: facetSlug,
        detail_name: typeof facet === 'object' ? facet.name : facetSlug,
        detail_value_name:
          value && typeof value === 'object' && 'name' in value
            ? value.name
            : '',
      });

      if (value == null) {
        newState = removeFromState(newState, facetSlug);
      } else {
        if (facetSlug === 'category') {
          newState = {
            ...newState,
            category:
              typeof value === 'string' ||
              typeof value === 'number' ||
              Array.isArray(value)
                ? [value]
                : [value.id],
          };
        } else {
          if (
            typeof value === 'string' ||
            typeof value === 'number' ||
            Array.isArray(value)
          ) {
            newValue = value;
          } else {
            newValue = singleValue ? value.id : [value.id];
          }

          if (isSelected(facet, value)) {
            newState = removeFromState(newState, facetSlug, newValue);
          } else {
            newState = addToState(newState, facetSlug, newValue);
          }
        }
      }

      facetUpdateQuery.current = newState;
      dispatch({
        type: 'UPDATED_FILTER_STATE',
        payload: { filterState: newState },
      });

      if (navigate) {
        navigateToUpdatedFilterState(newState);
      }

      if (inModal) {
        requestFacetUpdate();
      }
    },
    [dispatch, filterState, inModal, isSelected, navigateToUpdatedFilterState],
  );

  const requestFacetUpdate = useCallback(async () => {
    try {
      const facetResponse = await fetchFacetMetaNoItems(
        facetUpdateQuery.current,
      );

      dispatch({
        type: 'UPDATED_FACETS',
        payload: {
          facets: facetResponse.meta.facets,
          paging: facetResponse.meta.paging,
        },
      });
    } catch (err) {}
  }, [dispatch]);

  const clearAll = useCallback(() => {
    let newState: any;

    if (type === 'locker') {
      newState = {
        seller: facetUpdateQuery.current.seller,
        sort: facetUpdateQuery.current.sort,
        state: facetUpdateQuery.current.state,
      };
    } else if (lander) {
      newState = lander.query;
    } else {
      newState = {};
    }

    dispatch({
      type: 'CLEAR_ALL',
      payload: {
        filterState: newState,
      },
    });
    facetUpdateQuery.current = newState;
    requestFacetUpdate();
    navigateToUpdatedFilterState(newState);
  }, [
    dispatch,
    lander,
    navigateToUpdatedFilterState,
    requestFacetUpdate,
    type,
  ]);

  const setPrices = useCallback(
    (min?: number, max?: number) => {
      const newState = {
        ...evaluatedFilterState,
        min_price: min,
        max_price: max,
      };

      facetUpdateQuery.current = newState;

      dispatch({
        type: 'UPDATED_FILTER_STATE',
        payload: { filterState: newState },
      });

      navigateToUpdatedFilterState(newState);

      if (inModal) {
        requestFacetUpdate();
      }
    },
    [evaluatedFilterState, navigateToUpdatedFilterState],
  );

  const filterCount = useMemo(
    () => getActiveFacetsCount(evaluatedFilterState, sorts?.default_option),
    [evaluatedFilterState, sorts?.default_option],
  );

  const categories = useMemo(
    () =>
      (facets.find(f => f.slug === 'category')
        ?.children as TypedRailsFacetChild<RailsCategory>[]) ?? null,
    [facets],
  );

  const category = useMemo(
    () => getDetailById(categories, evaluatedFilterState?.category?.[0]),
    [categories, evaluatedFilterState],
  );

  const transferableFilterState = useMemo(
    () =>
      getTransferableFilterState(evaluatedFilterState, sorts?.default_option),
    [evaluatedFilterState, sorts?.default_option],
  );

  return {
    categories,
    category,
    changeFacetSelection,
    clearAll,
    facets,
    filterCount,
    isSelected,
    lander,
    paging,
    requestFacetUpdate,
    setPrices,
    similarToItem,
    sorts,
    transferableFilterState,
    type,
    unflattenedFilterState,
    filterState: evaluatedFilterState,
  };
}
